<template>
  <div class="py-5">
    <div class="container is-fluid">
      <div class="flex flex--row">
        <button
          @click.once="saveRfq()" 
          class="button is-primary mb-5 mr-4"
          :class="{ 'is-loading': saving }"
          :disabled="!valid || saving">
          <div>
            <span>Save&nbsp;</span>
            <span class="has-text-weight-bold">
              {{ $route.params.productBrand }}
            </span>
            <span>&nbsp;RFQ</span>
          </div>
        </button>
        <v-radio-button 
          :value="rfq.rfq_type" 
          @input="changeRfqType"
          :options="rfqTypes" 
        />
      </div>

      <v-form :valid.sync="valid" class="columns is-multiline">
        <div class="column is-3">
          <label class="label">RFQ Name*</label>
          <v-text-field
            required
            placeholder="RFQ Name"
            v-model="rfq.rfq_name"
          />
        </div>
        <div class="column is-3">
          <label class="label">Project*</label>
          <search-autocomplete 
            v-model="rfq.project_id"
            :product-brand="$route.params.productBrand"/>
        </div>
        <div class="column is-6">
          <label class="label">Dropbox Link to Drawings*</label>
          <v-text-field
            required
            placeholder="Link to Architectural Drawings"
            v-model="rfq.drawing"
            :rules="[
              v => v.includes('http') ? true : 
                'Please add https:// in front of the url']"/>
        </div>
        <div class="column is-3">
          <label class="label">Sales Person*</label>
          <v-select
            v-if="users.length > 0"
            required
            :options="users.filter(d => ['sales', 'sales-estimator', 'sales-estimator-samples'].includes(d.role))"
            item-text="display_name"
            item-value="id"
            v-model="rfq.main_salesperson_id"
          />
        </div>
        <div class="column is-3">
          <label class="label">Estimator*</label>
          <v-select
            v-if="users.length > 0"
            required
            item-text="display_name"
            item-value="id"
            :options="users.filter(d => ['estimator', 'sales-estimator', 'sales-estimator-samples'].includes(d.role))"
            v-model="rfq.main_estimator_id"
          />
        </div>
        <div class="column is-3">
          <label class="label">Quote Type*</label>
          <div v-if="rfq.rfq_type === CONDENSED">
            <v-select
              class="mt-1"
              :options="quoteTypes"
              :value="rfq.quote_type"
              @input="changeQuoteType"
              item-text="label"
              item-value="value"
              required
            />
          </div>
          <div class="flex flex--column" v-else>
            <v-checkbox 
              v-model="rfq.has_supply" 
              label="supply"/>
            <v-checkbox 
              v-model="rfq.has_install" 
              label="install"/>
          </div>
        </div>
        <div class="column is-3" v-if="rfq.rfq_type === CONDENSED">
          <label class="label">Stage*</label>
          <div>
            <v-select
              class="mt-1"
              :options="stages"
              required
              v-model="rfq.rfq_stage"
              item-text="label"
              item-value="value"
            />
          </div>
        </div>
        <div class="column is-3" v-if="rfq.rfq_type === CONDENSED">
          <label class="label">Purpose of Project*</label>
          <div>
            <v-text-field
              required
              class="mt-1"
              placeholder="Project Purpose"
              v-model="rfq.rfq_purpose"
            />
          </div>
        </div>
        <div class="column is-3" v-if="rfq.rfq_type === CONDENSED">
          <label class="label">Material Onsite Date*</label>
          <div>
            <v-text-field 
              required
              class="mt-1"
              placeholder="Material Onsite Date (e.g. Q4 -2023)"
              v-model="rfq.rfq_material_onsite_date"
            />
          </div>
        </div>
        <div class="column is-3" v-if="!isMitrex && rfq.rfq_type !== CONDENSED">
          <label class="label">Services*</label>
          <div class="flex flex--column">
            <v-checkbox 
              v-for="service in services" 
              :key="service.id"
              :label="service.service_name"
              :value="rfq.services.includes(service.id)"
              @input="toggleService(service.id)"/>
          </div>
        </div>
        <div class="column is-full" v-if="filteredDocumentTypes.length > 0">
          <label class="label">Documents Required*</label>
          <div class="flex flex--row">
            <div 
              v-for="document in filteredDocumentTypes"
              :key="document.id">
              <v-checkbox 
                class="mr-4"
                :label="document.document_type_name"
                :value="rfq.documents.map(d => d.id).includes(document.id)"
                @input="toggleDocument(document)"/>
              <template v-if="rfq.documents.map(d => d.id).includes(document.id)">
                <v-datepicker-modal-slot
                  :value="rfq.documents.filter(d => d.id == document.id)[0].received_on"
                  @input="updateDocumentDate(document.id, $event)">
                  <a class="mt-1 ml-4 mr-2 help">
                    <span v-if="!rfq.documents.filter(d => d.id == document.id)[0].received_on">
                      Select Date Received
                    </span>
                    <span v-else>
                      {{ formatDate(rfq.documents.filter(d => d.id == document.id)[0].received_on) }}
                    </span>
                  </a>
                </v-datepicker-modal-slot>
              </template>
            </div>
          </div>
        </div>
      </v-form>
    </div>
    <hr>

    <div class="container is-fluid pt-3">
      <div class="columns item-header">
        <div class="column label py-0">
          <label class="label">Item</label>
        </div>
        <div 
          class="column label py-0">
          <label class="label">Materials</label>
        </div>
        <div 
          v-if="rfq.rfq_type === 'full'"
          class="column label py-0">
          <label class="label">Fabrication</label>
        </div>
        <div 
          v-if="rfq.has_install"
          class="column label py-0">
          <label class="label">Installation</label>
        </div>
      </div>

      <div 
        v-for="(item, itemIndex) in items" 
        :key="itemIndex"
        class="columns is-multiline">
        <div class="column">
          <div class="item is-outlined is-relative">
            <div 
              @click="items.splice(itemIndex, 1)" 
              class="delete"></div>
            <div>
              <label class="is-size-7">Architectural Name</label>
              <v-text-field 
                required
                class="mt-1"
                placeholder="Architectural Name"
                v-model="item.architecture_name"/>
            </div>
          </div>
        </div>

        <div 
          class="column">

          <div
            v-for="(material, materialIndex) in item.materials"
            :key="materialIndex" 
            class="sub-item is-outlined mb-2">
            <product-selector 
              v-if="material.type == 'product'"
              v-model="material.id"
              show-category
              disable-category
              :has-product-search="material.product_category_id == 13"
              :product-category-id="material.product_category_id"/>

            <template v-if="material.type == 'processing'">
              <div>
                <div class="menu-label mb-0">
                  {{ material.type }}
                </div>
                <v-select
                  class="mt-1"
                  is-small
                  v-model="material.processing_id"
                  :options="processingMethods.filter(
                    d => d.processing_type_id == material.processing_type_id)"
                  item-text="processing_name"
                  item-value="id"
                />
              </div>
            </template>

            <template v-if="material.type == 'finishing'">
              <div>
                <div class="menu-label mb-0">
                  {{ material.type }}
                </div>
                <v-select
                  class="mt-1"
                  is-small
                  v-if="rfq.rfq_type !== CONDENSED"
                  v-model="material.processing_id"
                  :options="processingMethods.filter(
                    d => d.processing_type_id == material.processing_type_id)"
                  item-text="processing_name"
                  item-value="id"
                />
              </div>
            </template>

            <div 
              @click="deleteSubItem(itemIndex, 'materials', materialIndex)" 
              class="delete ml-2">
            </div>
          </div>

          <div 
            v-if="addingFinishing.includes(itemIndex) || item.finishing_name" 
            class="sub-item is-outlined mb-2"
          >
            <div>
              <div class="menu-label mb-0">
                Finishing
              </div>
              <v-text-field 
                placeholder="Enter finishing name"
                v-model="item.finishing_name"
                is-small
                is-expanded
                class="mt-1"
              />
            </div>
            <div 
              @click="exitAddFinishing(item, itemIndex)" 
              class="delete ml-2">
            </div>
          </div>

          <div>
            <div
              @click="addBacking(itemIndex)" 
              v-if="!item.materials.map(d => d.product_category_id).includes(14) && 
                !item.materials.map(d => d.product_category_id).includes(20) &&
                rfq.rfq_type !== CONDENSED"
              class="lg-add-btn is-size-7 mb-2">
              <span class="icon">
                <i class="fa fa-plus"></i>
              </span>
              <span>Backing</span>
            </div>
            <div
              @click="addFacing(itemIndex)"  
              v-if="!item.materials.map(d => d.product_category_id).includes(13) && 
                !item.materials.map(d => d.product_category_id).includes(20)"
              class="lg-add-btn is-size-7 mb-2">
              <span class="icon">
                <i class="fa fa-plus"></i>
              </span>
              <span>Facing</span>
            </div>
            <div
              @click="addRailing(itemIndex)" 
              v-if="!item.materials.map(d => d.product_category_id).includes(20) && 
                !item.materials.map(d => d.product_category_id).includes(14) &&
                !item.materials.map(d => d.product_category_id).includes(13)"
              class="lg-add-btn is-size-7 mb-2">
              <span class="icon">
                <i class="fa fa-plus"></i>
              </span>
              <span>Railing</span>
            </div>
            <div
              @click="addProcessing(itemIndex)" 
              v-if="!item.materials.map(
                d => d.processing_type_id).includes(1) && 
                rfq.rfq_type !== CONDENSED"
              class="lg-add-btn is-size-7 mb-2">
              <span class="icon">
                <i class="fa fa-plus"></i>
              </span>
              <span>Processing</span>
            </div>
            <div 
              key="add-finishing"
              @click="
                rfq.rfq_type === CONDENSED 
                  ? addingFinishing.push(itemIndex)
                  : addFinishing(itemIndex)
              " 
              v-if="(rfq.rfq_type !== CONDENSED && !item.materials.map(
                  d => d.processing_type_id).includes(2)) 
                || (rfq.rfq_type === CONDENSED && !addingFinishing.includes(itemIndex))"
              class="lg-add-btn is-size-7 mb-2">
              <span class="icon">
                <i class="fa fa-plus"></i>
              </span>
              <span>Finishing</span>
            </div>
          </div>
        </div>

        <div 
          v-if="rfq.rfq_type === 'full'"
          class="column">
          <div
            v-for="(fabrication, fabricationIndex) in item.fabrications"
            :key="fabricationIndex" 
            class="sub-item is-outlined mb-2">
            <div>
              <div class="menu-label mb-0">
                {{ fabrication.fabrication_type.fabrication_type_name }}
              </div>
              <v-select
                class="mt-1"
                is-small
                v-model="fabrication.id"
                :options="fabrications.filter(
                  d => d.fabrication_type_id == fabrication.fabrication_type.id)"
                item-text="fabrication_name"
                item-value="id"
              />
            </div>
            <div 
              @click="deleteSubItem(itemIndex, 'fabrications', fabricationIndex)" 
              class="delete ml-2">
            </div>
          </div>

          <div>
            <template 
              v-for="fabricationType in fabricationTypes">
              <div
                :key="fabricationType.id" 
                @click="addFabrication(itemIndex, fabricationType)"
                v-if="!item.fabrications.map(
                  d => d.fabrication_type.id).includes(fabricationType.id)"
                class="lg-add-btn is-size-7 mb-2">
                <span class="icon">
                  <i class="fa fa-plus"></i>
                </span>
                <span>
                  {{ fabricationType.fabrication_type_name }}
                </span>
              </div>
            </template>
          </div>
        </div>

        <div 
          v-if="installationTypes.length > 0 && rfq.has_install"
          class="column">
          <template
            v-for="(installation, installationIndex) in item.installations">
            <div
              v-if="[1,2].includes(installation.installation_type.id)"
              :key="installationIndex" 
              class="sub-item is-outlined mb-2">
              <div>
                <div class="menu-label mb-0">
                  {{ installation.installation_type.installation_type_name }}
                </div>
                <v-select
                  class="mt-1"
                  is-small
                  v-model="installation.id"
                  :options="installations.filter(
                    d => d.installation_type_id == installation.installation_type.id)"
                  item-text="installation_name"
                  item-value="id"
                />
              </div>
              <div 
                @click="deleteSubItem(itemIndex, 'installations', installationIndex)" 
                class="delete ml-2">
              </div>
            </div>
          </template>
          <div v-if="rfq.rfq_type !== CONDENSED">
            <v-checkbox 
              class="is-size-7"
              v-for="installationType in installationTypes.filter(
                d => ![1,2].includes(d.id))" 
              :key="installationType.id"
              :value="item.installations.map(
                d => d.installation_type.id
              ).includes(installationType.id)" 
              @input="toggleInstallationType(itemIndex, installationType)"
              :label="installationType.installation_type_name"/>
          </div>
        </div>

        <div v-if="itemIndex != items.length-1" class="column is-full py-0">
          <hr class="mt-0 mb-4">
        </div>

      </div>

      <div
        class="columns">
        <div class="column is-full">
          <div
            @click="addItem" 
            class="lg-add-btn spaced">
            <span class="icon">
              <i class="fa fa-plus"></i>
            </span>
            <span>Add an item</span>
          </div>
        </div>
      </div>

    </div>
    <!-- <hr>

    <div class="container is-fluid">
      <div class="subtitle">Services</div>
      <div
        class="columns is-multiline">
        <div 
          v-for="service in services"
          :key="service.id"
          class="column is-full py-1">
          <div class="lg-add-btn">
            <span class="icon">
              <i class="fa fa-plus"></i>
            </span>
            <span>Add {{ service.service_name }}</span>
          </div>
        </div>
      </div>
    </div> -->
  </div>
</template>

<script>
import VForm from '@/components/base/VForm'
import VSelect from '@/components/base/VSelect'
import VTextField from '@/components/base/VTextField'
import VCheckbox from '@/components/base/VCheckbox'
import ProductSelector from '@/components/inventory/ProductSelector'
import SearchAutocomplete from '@/components/projects/SearchAutocomplete'
import VDatepickerModalSlot from '@/components/base/VDatepickerModalSlot'
import VRadioButton from '@/components/base/VRadioButton'
import { mapState, mapActions, mapMutations } from 'vuex'
import { format } from 'date-fns'
import { CONDENSED } from '@/helpers/constants'
import rfqCondensable from '@/mixins/rfqCondensable'

export default {
  mixins: [rfqCondensable],
  components: {
    VForm, VSelect, VTextField, VCheckbox, SearchAutocomplete, VDatepickerModalSlot,
    VRadioButton, ProductSelector
  },
  data() {
    return {
      rfq: {
        rfq_name: null,
        rfq_type: CONDENSED,
        rfq_stage: null,
        rfq_purpose: null,
        rfq_material_onsite_date: null,
        project_id: null,
        drawing: null,
        main_salesperson_id: null,
        main_estimator_id: 53,
        quote_type: null,
        has_supply: true,
        has_install: true,
        services: [],
        documents: []
      },
      itemTemplate: {
        item_name: "",
        architecture_name: "",
        materials: [],
        fabrications: [],
        installations: []
      },
      items: [],
      saving: false,
      valid: false,
      formatDate: (d) => {
        return d ? format(d, 'yyyy-MM-dd') : d;
      },
      addingFinishing: []
    }
  },
  computed: {
    ...mapState("users", ["users"]),
    ...mapState("auth", ["user"]),
    ...mapState("services", ["services"]),
    ...mapState("processings", ["processingMethods"]),
    ...mapState("rfqDocumentTypes", ["documentTypes"]),
    ...mapState("fabrications", ["fabrications", "fabricationTypes"]),
    ...mapState("installations", ["installations", "installationTypes"]),
    isMitrex() {
      return this.$route.params.productBrand == 'Mitrex'
    },
    filteredDocumentTypes({ rfq, documentTypes }) {
      return documentTypes.filter(t => 
        rfq.rfq_type === CONDENSED ? t.is_for_condensed_proposal : t.is_for_full_proposal
      )
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("users", ["getUsers"]),
    ...mapActions("rfqs", ["addRfq"]),
    ...mapActions("services", ["getServices"]),
    ...mapActions("processings", ["getProcessingMethods"]),
    ...mapActions("rfqDocumentTypes", ["getDocumentTypes"]),
    ...mapActions("fabrications", ["getFabrications", "getFabricationTypes"]),
    ...mapActions("installations", ["getInstallations", "getInstallationTypes"]),
    ...mapActions("drawingHighlights", ["addDrawingHighlight"]),
    ...mapMutations("rfqs", ["addHighlightToRfq"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.$route?.params?.data?.rfqType) {
        this.rfq.rfq_type = this.$route.params.data.rfqType;
      }
      this.rfq.main_salesperson_id = this.user.id;
      this.getUsers({ per_page: 100, is_active: true });

      if (this.services.length == 0) {
        this.getServices({ per_page: 100 });
      }

      if (this.processingMethods.length == 0) {
        this.getProcessingMethods({ per_page: 100 });
      }

      if (this.documentTypes.length == 0) {
        this.getDocumentTypes();
      }

      if (this.fabrications.length == 0) {
        this.getFabrications({ per_page: 100 });
      }

      if (this.fabricationTypes.length == 0) {
        this.getFabricationTypes();
      }

      if (this.installations.length == 0) {
        this.getInstallations({ per_page: 100 });
      }

      if (this.installationTypes.length == 0) {
        this.getInstallationTypes().then(() => {
          this.addDefaultInstallationTypes();
          this.addItem();
        })
      } else {
        this.addDefaultInstallationTypes();
        this.addItem();
      }
    },
    toggleService(serviceId) {
      let ind = this.rfq.services.indexOf(serviceId);
      if (ind == -1) {
        this.rfq.services.push(serviceId);
      } else {
        this.rfq.services.splice(ind, 1);
      }
    },
    toggleDocument(document) {
      let ind = this.rfq.documents.map(d => d.id).indexOf(document.id);
      if (ind == -1) {
        this.rfq.documents.push({ id: document.id, received_on: null });
      } else {
        this.rfq.documents.splice(ind, 1);
      }
    },
    updateDocumentDate(documentId, v) {
      this.rfq.documents.forEach(document => {
        if (document.id == documentId) {
          document.received_on = v;
        }
      })
    },
    addItem() {
      this.items.push(JSON.parse(JSON.stringify(this.itemTemplate)));
    },
    deleteSubItem(itemIndex, section, subItemIndex) {
      this.items[itemIndex][section].splice(subItemIndex, 1);
    },
    addDefaultInstallationTypes() {
      let method = this.installationTypes.filter(d => d.id == 1)[0];
      this.itemTemplate.installations.push(
        { id: null, installation_type: method }
      );

      if (this.rfq.rfq_type !== CONDENSED) {
        let system = this.installationTypes.filter(d => d.id == 2)[0];
        this.itemTemplate.installations.push(
          { id: null, installation_type: system }
        );
      }
    }, 
    addBacking(i) {
      this.items[i].materials.push(
        { id: null, type: 'product', product_category_id: 14 }
      );
    },
    addFacing(i) {
      this.items[i].materials.push(
        { id: null, type: 'product', product_category_id: 13 }
      );
    },
    addRailing(i) {
      this.items[i].materials.push(
        { id: null, type: 'product', product_category_id: 20 }
      );
    },
    addProcessing(i) {
      this.items[i].materials.push(
        { processing_id: null, processing_type_id: 1, type: 'processing' }
      );
    },
    addFinishing(i) {
      this.items[i].materials.push(
        { processing_id: null, processing_type_id: 2, type: 'finishing' }
      );
    },
    addFabrication(i, fabricationType) {
      this.items[i].fabrications.push(
        { id: null, fabrication_type: fabricationType }
      );
    },
    toggleInstallationType(i, installationType) {
      let existingInstallationTypes = this.items[i].installations.map(
        d => d.installation_type.id
      )
      let index = existingInstallationTypes.indexOf(installationType.id);
      if (index == -1) {
        let installations = this.installations.filter(
          d => d.installation_type_id == installationType.id
        );
        let id = null;
        if (installations.length == 1) {
          id = installations[0].id
        }
        this.items[i].installations.push(
          { id: id, installation_type: installationType }
        )
      } else {
        this.items[i].installations.splice(index, 1);
      }
    },
    changeRfqType(type) {
      if (type === CONDENSED) {
        this.itemTemplate.installations = this.itemTemplate.installations.filter(
          it => it.installation_type.id !== 2
        )
        this.items.forEach(item => {
          item.installations = item.installations.filter(inst => inst.installation_type.id !== 2)
        })
      } else {
        const installationSys = this.itemTemplate.installations.find(it => it.installation_type.id === 2)
        const system = this.installationTypes.filter(d => d.id == 2)[0]
        if (!installationSys) {
          this.itemTemplate.installations.push(
            { id: null, installation_type: system }
          );
        }
        this.items.forEach(item => {
          let itemSys = item.installations.find(d => d.installation_type === 2)
          if (!itemSys) {
            item.installations.push(
              { id: null, installation_type: system }
            );
          }
        })
      }
      this.rfq.rfq_type = type
    },
    changeQuoteType(quoteType) {
      const quoteTypeSelected = this.quoteTypes.find(t => t.value === quoteType)
      this.rfq.quote_type = quoteType
      this.rfq.has_supply = quoteTypeSelected.has_supply
      this.rfq.has_install = quoteTypeSelected.has_install
    },
    saveRfq() {
      this.saving = true;
      const { rfq } = this;

      this.$http.cladifyRfqs.post({
        rfq_name: rfq.rfq_name,
        rfq_type: rfq.rfq_type,
        rfq_stage: rfq.rfq_stage,
        rfq_purpose: rfq.rfq_purpose,
        rfq_material_onsite_date: rfq.rfq_material_onsite_date,
        project_id: rfq.project_id,
        drawing: rfq.drawing,
        main_salesperson_id: rfq.main_salesperson_id,
        main_estimator_id: rfq.main_estimator_id,
        quote_type: rfq.quote_type,
        has_supply: rfq.has_supply,
        has_install: rfq.has_install,
        product_brand: this.$route.params.productBrand
      }).then(
        resp => {
          let promises = [];

          // Add Drawing Highlight Request
          promises.push(
            this.addDrawingHighlight({ 
              "project_rfq_id": resp.data.id,
              "is_requested": true
            }).then(resp => {
              this.addHighlightToRfq(resp.data);
            })
          )

          // Add Documents
          this.rfq.documents.forEach(document => {
            promises.push(
              this.$http.rfqDocuments.post({ 
                project_rfq_id: resp.data.id,
                document_type_id: document.id,
                received_on: document.received_on
              })
            )
          })

          // Add items
          const ESTIMATION_ITEMS = this.isMitrex ? 
            'estimationItemsMitrex' : 'estimationItems';
          const ESTIMATION_ITEM = this.isMitrex ? 
            'estimationItemMitrex' : 'estimationItem';
          const ESTIMATION_ITEM_MATERIALS = this.isMitrex ? 
            'estimationItemMaterialsMitrex' : 'estimationItemMaterials';
          const ESTIMATION_ITEM_PROCESSINGS = this.isMitrex ? 
            'estimationItemProcessingsMitrex' : 'estimationItemProcessings';
          const ESTIMATION_ITEM_FABRICATIONS = this.isMitrex ? 
            'estimationItemFabricationsMitrex' : 'estimationItemFabrications';
          const ESTIMATION_ITEM_INSTALLATIONS = this.isMitrex ? 
            'estimationItemInstallationsMitrex' : 'estimationItemInstallations';
          const ESTIMATION_SERVICES = this.isMitrex ? 
            'estimationServicesMitrex' : 'estimationServices';

          this.items.forEach((item, i) => {
            promises.push(
              this.$http[ESTIMATION_ITEMS].post({
                estimation_id: resp.data.c_estimation.id,
                architecture_name: item.architecture_name,
                finishing_name: item.finishing_name,
                order: i
              }).then(itemResp => {
                // Add item materials
                item.materials.forEach(material => {
                  if (material.type == 'product') {
                    this.$http[ESTIMATION_ITEM_MATERIALS].post({
                      estimation_item_id: itemResp.data.id,
                      product_master_id: material.id
                    })
                  } else {
                    this.$http[ESTIMATION_ITEM_PROCESSINGS].post({
                      estimation_item_id: itemResp.data.id,
                      processing_id: material.processing_id
                    })
                  }
                })

                // Add item fabrications
                item.fabrications.forEach(fabrication => {
                  this.$http[ESTIMATION_ITEM_FABRICATIONS].post({
                    estimation_item_id: itemResp.data.id,
                    fabrication_id: fabrication.id
                  })
                })

                // Add item installations
                item.installations.forEach(installation => {
                  if (installation.installation_type.id == 3) {
                    this.$http[ESTIMATION_ITEM].put(
                      itemResp.data.id, { has_insulation: true })
                  } else if (installation.id != null) {
                    this.$http[ESTIMATION_ITEM_INSTALLATIONS].post({
                      estimation_item_id: itemResp.data.id,
                      installation_id: installation.id
                    })
                  }
                })
              })
            )
          })

          this.rfq.services.forEach(service => {
            promises.push(
              this.$http[ESTIMATION_SERVICES].post({
                estimation_id: resp.data.c_estimation.id,
                service_id: service
              })
            )
          })

          Promise.all(promises).then(() => {
            this.setNotification({
              status: true,
              type: 'success',
              message: 'RFQ Successfully Saved'
            })
            this.saving = false;
            this.$router.push({
              name: 'RfqViewer',
              params: {
                rfqId: resp.data.id
              }
            })
          }).catch(() => {
            this.saving = false;
          })
        }
      ).catch(() => this.saving = false);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/flexbox.scss';

.grouped-buttons {
  display: flex;
  flex-direction: row;
  margin: 0em -5px;
}

.lg-add-btn {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  background: rgb(240, 240, 240);
  padding: 0.5em;
  border-radius: 4px;
  &:hover {
    background: #eee;
  }
  &.grouped {
    width: 25%;
    margin: 0px 5px;
  }
  &.spaced {
    padding: 1em;
  }
}

.is-outlined {
  border: 1px solid gainsboro;
  border-radius: 4px;
}

.item {
  border-radius: 4px;
  padding: 0.75em;
  flex-grow: 1;

  .delete {
    position: absolute !important;
    top: 5px;
    right: 5px;
  }
}

.sub-item {
  border-radius: 4px;
  padding: 0.5em;
  margin-bottom: 0.5em;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.item-header {
  position: sticky;
  top: 0px;
  background: #fff;
  z-index: 5;
}
</style>